import { formatCurrency } from "@/lib/currency";
import { useState } from "react";
import { Check, Building2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { EnterpriseContactForm } from "./EnterpriseContactForm";

const tiers = [
  {
    name: "Starter",
    price: 49,
    jobLimit: "75 jobs/month",
    description: "For independent operators and small teams.",
    features: [
      "Up to 75 jobs per month",
      "Automatic invoicing when jobs complete",
      "Unlimited driver accounts",
      "Real-time GPS tracking",
      "Digital appraisals with photos",
      "Automated SMS customer updates",
      "QuickBooks, Xero & FreeAgent sync",
      "In-app help guides",
    ],
    popular: false,
    tierKey: "starter",
    extraNote: "Best for 1–3 vehicles",
  },
  {
    name: "Growth",
    price: 99,
    jobLimit: "200 jobs/month",
    description: "For growing fleets with AI damage detection.",
    features: [
      "Up to 200 jobs per month",
      "Everything in Starter, plus:",
      "Automatic invoicing when jobs complete",
      "AI Damage Detection",
      "Custom branding & email templates",
      "Analytics dashboard",
      "Vehicle daily checks",
      "Multi-driver coordination",
      "Offline sync capability",
    ],
    popular: true,
    tierKey: "growth",
    extraNote: "Ideal for 4–10 vehicles",
  },
  {
    name: "Pro",
    price: 149,
    jobLimit: "Unlimited jobs",
    description: "Full control for high-volume operations.",
    features: [
      "Unlimited jobs per month",
      "Everything in Growth, plus:",
      "Automatic invoicing when jobs complete",
      "Advanced analytics & reporting",
      "Team performance insights",
      "MOT expiry reminders",
      "Overdue invoice reminders",
      "Auto-archive completed jobs",
      "Full feature access",
    ],
    popular: false,
    tierKey: "pro",
    extraNote: "Designed for up to 15 vehicles",
  },
];

export const LandingPricingTiers = () => {
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Stacked on mobile, Growth first */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
        {tiers.map((tier) => (
          <Card
            key={tier.name}
            className={`relative p-5 sm:p-6 lg:p-7 overflow-visible transition-[transform,box-shadow] ${
              tier.popular
                ? "border-primary border-2 md:scale-[1.03] md:-mt-2 order-first md:order-none"
                : "border"
            }`}
            style={{ boxShadow: tier.popular ? 'var(--shadow-glow-primary)' : 'var(--shadow-card)' }}
          >
            {tier.popular && (
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-bold tracking-wide uppercase">
                Most Popular
              </div>
            )}

            <div className="text-center mb-5">
              <h3 className="text-lg sm:text-xl font-extrabold mb-1">{tier.name}</h3>
              <p className="text-xs text-muted-foreground mb-3">
                {tier.description}
              </p>
              <div className="mb-1.5">
                <span className="text-3xl sm:text-4xl font-extrabold">{formatCurrency(tier.price)}</span>
                <span className="text-muted-foreground text-sm">/month</span>
              </div>
              <div className="inline-block bg-secondary text-secondary-foreground px-2.5 py-0.5 rounded-full text-xs font-semibold">
                {tier.jobLimit}
              </div>
              {tier.extraNote && (
                <p className="text-[11px] text-muted-foreground mt-1.5">{tier.extraNote}</p>
              )}
            </div>

            {/* What happens on completion */}
            <div className="bg-primary/5 border border-primary/10 rounded-lg p-3 mb-4">
              <p className="text-[11px] font-bold text-primary mb-1.5 uppercase tracking-wide">When a job completes:</p>
              <div className="flex flex-col gap-1">
                {["Proof sent automatically", "Invoice created & synced", "Payment tracked"].map((b, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs text-foreground font-medium">
                    <Check className="h-3 w-3 text-primary flex-shrink-0" /> {b}
                  </span>
                ))}
              </div>
            </div>

            <ul className="space-y-2 mb-5">
              {tier.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm leading-snug">{feature}</span>
                </li>
              ))}
            </ul>

            <Link to={`/auth?mode=signup&tier=${tier.tierKey}`} className="block">
              <Button
                className="w-full h-11 font-bold"
                size="lg"
                variant={tier.popular ? "default" : "outline"}
              >
                Start 30-Day Free Trial
                {tier.popular && <ArrowRight className="ml-1.5 h-4 w-4" />}
              </Button>
            </Link>
          </Card>
        ))}
      </div>

      {/* Enterprise */}
      <div className="mt-8 border-2 border-dashed border-muted-foreground/20 rounded-2xl p-5 sm:p-8 text-center bg-card">
        <div className="inline-block bg-primary/10 text-primary px-3 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide mb-2">
          Built for growing fleets
        </div>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h3 className="text-lg sm:text-xl font-extrabold">Enterprise</h3>
          <span className="text-[11px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">Large Fleets</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
          For organisations managing 15+ vehicles or multi-site operations.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-sm mx-auto mb-4 text-left">
          {[
            "Unlimited jobs & AI damage detection",
            "Priority onboarding & support",
            "Advanced reporting & integrations",
            "Multi-depot fleet management",
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              <span className="text-xs">{f}</span>
            </div>
          ))}
        </div>
        <Button variant="outline" size="lg" className="w-full sm:w-auto h-11 font-semibold" onClick={() => setEnterpriseOpen(true)}>
          Contact Sales
        </Button>
      </div>

      <p className="text-center text-muted-foreground mt-6 text-xs">
        30-day free trial on all plans • No charge until trial ends • Cancel anytime
      </p>

      <EnterpriseContactForm open={enterpriseOpen} onOpenChange={setEnterpriseOpen} />
    </div>
  );
};
